import { axios, getResponse, settings } from '../../helpers/main';

const { backend } = settings;
const objectSerice = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the Devices API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export const DeviceService = {
    /**
     * Retrieves all devices.
     * @param {Object} params - Query parameters.
     * @param {Object} params.auth - User authentication object.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    getAll: async ({
        auth,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.devices}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                method: backend?.methods?.devices.getAll,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Retrieves a device by ID.
     * @param {Object} params - Query parameters.
     * @param {Object} params.auth - User authentication object.
     * @param {number|string} params.id - Device ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    getById: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                id: id,
                languageId: languageId,
                method: backend?.methods?.devices.getById,
            },
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.devices}/`,
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Deletes a device by ID.
     * @param {Object} params - Query parameters.
     * @param {Object} params.auth - User authentication object.
     * @param {number|string} params.id - Device ID.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    deleteAsync: async ({
        auth,
        id,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.devices}/${id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.delete(requestObj);
    },

    /**
     * Creates a new device.
     * @param {Object} params - Query parameters.
     * @param {Object} params.auth - User authentication object.
     * @param {Object} params.data - Device data.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    create: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.devices}/`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },

    /**
     * Updates an existing device.
     * @param {Object} params - Query parameters.
     * @param {Object} params.auth - User authentication object.
     * @param {Object} params.data - Device data.
     * @param {number} params.languageId - Current language ID.
     * @param {string} [params.subMethod=''] - Sub-method name for error traceability.
     * @returns {Promise<objectSerice>} Promise with the API response.
     */
    update: async ({
        auth,
        data,
        languageId,
        subMethod = '',
    } = {}) => {
        const requestObj = {
            url: `${backend?.urls?.api}${backend?.urls?.apiV1}${backend?.urls?.devices}/${data.id}`,
            headers: {
                authorization: `bearer ${auth?.user?.token?.token}`,
            },
            data: {
                data: data,
                languageId: languageId,
            },
            subMethod: subMethod,
        };
        return await axios.post(requestObj);
    },
};
